import { useState } from "react";
import Head from "next/head";
import { FAQPageJsonLd, NextSeo } from "next-seo";
import { PrismicRichText } from "@prismicio/react";
import { Header } from "components/headers/Header";
import BackgroundGlassmorphism from "components/backgrounds/BackgroundGlassmorphism";
import Footer from "components/footer/Footer";
import richTextComponents from "helpers/richTextComponents";
import { createClient } from "../prismic";

const Item = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b">
      <button
        type="button"
        aria-label="Open item"
        title="Open item"
        className="flex items-center justify-between w-full p-4 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <p className="text-lg font-medium">{title}</p>
        <svg
          viewBox="0 0 24 24"
          className={`w-3 text-gray-600 transform transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          <polyline
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeMiterlimit="10"
            points="2,7 12,17 22,7"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {isOpen && (
        <div className="p-4 pt-0">
          <div className="text-gray-700 leading-relaxed space-y-4">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const FAQPage = ({ page }) => {
  const {
    title,
    description,
    openGraphTitle,
    openGraphDesc,
    openGraphImage,
    FAQGroup,
  } = page.data;

  return (
    <div className="nc-PageFAQPage relative overflow-hidden">
      <Head>
        <meta name="robots" content="all" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
        />
      </Head>
      <NextSeo
        title={`${
          title ? title : "De vakantiehuizen website van Europa"
        } | TopVakantiehuizen.eu`}
        description={
          description
            ? description
            : "Op zoek naar een vakantiehuis in Europa? Op TopVakantiehuizen.eu vind jij het perfecte vakantiehuis! Boek veilig & snel bij TopVakantiehuizen.eu!"
        }
        canonical={`https://www.topvakantiehuizen.eu/veelgestelde-vragen`}
        openGraph={{
          url: `https://topvakantiehuizen.eu/${page.uid}`,
          title: `${
            openGraphTitle
              ? openGraphTitle
              : "De vakantiehuizen website van Europa"
          } | TopVakantiehuizen.eu`,
          description: `${
            openGraphDesc
              ? openGraphDesc
              : "Op zoek naar een vakantiehuis in Europa? Op TopVakantiehuizen.eu vind jij het perfecte vakantiehuis! Boek veilig & snel bij TopVakantiehuizen.eu!"
          }`,
          images: [
            {
              url: openGraphImage?.url
                ? openGraphImage.url
                : "https://topvakantiehuizen.eu/assets/logo/TopVakantiehuizen.eu.png",
            },
          ],
          site_name: "TopVakantiehuizen.eu",
        }}
        twitter={{
          handle: "@handle",
          site: "@site",
          cardType: "summary_large_image",
        }}
      />

      <FAQPageJsonLd
        mainEntity={FAQGroup.map((group) => ({
          questionName: group.title,
          acceptedAnswerText: group.shortSEOAnswer,
        }))}
      />

      <Header />
      <BackgroundGlassmorphism />
      <main className="container relative mb-24 space-y-24 lg:mb-32 lg:space-y-32">
        <div className="px-4 py-16 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8 lg:py-20">
          <div className="max-w-xl sm:mx-auto lg:max-w-2xl">
            <div className="max-w-xl mb-10 md:mx-auto sm:text-center lg:max-w-2xl md:mb-12">
              <div>
                <p className="inline-block px-3 py-px mb-4 text-xs font-semibold tracking-wider text-teal-900 uppercase rounded-full bg-teal-accent-400">
                  FAQ
                </p>
              </div>
              <h2 className="max-w-lg mb-6 font-sans text-3xl font-bold leading-none tracking-tight text-gray-900 sm:text-4xl md:mx-auto">
                <span className="relative inline-block">
                  <svg
                    viewBox="0 0 52 24"
                    fill="currentColor"
                    className="absolute top-0 left-0 z-0 hidden w-32 -mt-8 -ml-20 text-blue-gray-100 lg:w-32 lg:-ml-28 lg:-mt-10 sm:block"
                  >
                    <defs>
                      <pattern
                        id="232db96b-4aa2-422f-9086-5a77996d1df1"
                        x="0"
                        y="0"
                        width=".135"
                        height=".30"
                      >
                        <circle cx="1" cy="1" r=".7" />
                      </pattern>
                    </defs>
                    <rect
                      fill="url(#232db96b-4aa2-422f-9086-5a77996d1df1)"
                      width="52"
                      height="24"
                    />
                  </svg>
                </span>{" "}
                Veelgestelde vragen
              </h2>
              <p className="text-base text-gray-700 md:text-lg">
                Vind hier al uw antwoorden op veelgestelde vragen.
              </p>
            </div>
            <div className="space-y-4">
              {FAQGroup.map((group, index) => (
                <Item title={group.title} key={index}>
                  <PrismicRichText
                    field={group.description}
                    components={richTextComponents}
                  />
                </Item>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export async function getStaticProps({ previewData }) {
  const client = createClient({ previewData });
  const page = await client.getSingle("faq-page");

  return {
    props: { page }, // Will be passed to the page component as props
  };
}

export default FAQPage;
